import { fromEnumAsString } from '../../../serialization/enum-utils.js';
import { MapUtils } from '../../../utility/map-utils.js';
import { VersionRange } from '../../core/version-range.js';
import { cloneList, DataModel, DataModelBuilder, isSameList, SerializationContext, SerializedData } from '../../dto/dto.js';
import { LegacyOrderHook, LegacyOrderHookId } from '../legacy-order-hook.js';
import { OrderSideEffectType } from './order-side-effect-type.js';
import { OrderSideEffect } from './order-side-effect.js';

export class CreateOpenAccessPassFromTicketSideEffect extends OrderSideEffect {
  static readonly TYPE_ID: string = 'cbe94102540242fbaa73a3a934905635';
  static readonly SCHEMA_VERSION: number = 2;

  public readonly effectType: OrderSideEffectType = OrderSideEffectType.CreateOpenAccessPassFromTicket;

  private constructor(
    public readonly passTemplateIds: string[],
  ) {
    super();
  }

  get $typeId(): string {
    return CreateOpenAccessPassFromTicketSideEffect.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.between(1, CreateOpenAccessPassFromTicketSideEffect.SCHEMA_VERSION);
  }

  static create(passTemplateIds: string[]): CreateOpenAccessPassFromTicketSideEffect {
    return new CreateOpenAccessPassFromTicketSideEffect(passTemplateIds);
  }

  static legacy(): CreateOpenAccessPassFromTicketSideEffect {
    return new CreateOpenAccessPassFromTicketSideEffect([]);
  }

  static fromMap(context: SerializationContext, map: SerializedData): CreateOpenAccessPassFromTicketSideEffect {
    return new CreateOpenAccessPassFromTicketSideEffect(
        MapUtils.getList(map, 'passTemplateIds', (v) => v as string),
    );
  }

  toMap(_context: SerializationContext): SerializedData {
    return {
      'effectType': fromEnumAsString(this.effectType),
      'passTemplateIds': this.passTemplateIds,
    };
  }

  clone(): this {
    return new CreateOpenAccessPassFromTicketSideEffect(
        cloneList(this.passTemplateIds),
    ) as this;
  }

  isSame(other: CreateOpenAccessPassFromTicketSideEffect): boolean {
    if(this === other) {
      return true;
    }

    return other.effectType === this.effectType &&
      isSameList(other.passTemplateIds, this.passTemplateIds);
  }

  toLegacyOrderHook(): LegacyOrderHook {
    return LegacyOrderHook.create(LegacyOrderHookId.CreateOpenAccessPassFromTicket);
  }

  static verifyType(order: DataModel): boolean {
    return order instanceof CreateOpenAccessPassFromTicketSideEffect;
  }

  static assertType(order: DataModel): order is CreateOpenAccessPassFromTicketSideEffect {
    return order instanceof CreateOpenAccessPassFromTicketSideEffect;
  }
}

export class CreateOpenAccessPassFromTicketSideEffectBuilder extends DataModelBuilder<CreateOpenAccessPassFromTicketSideEffect> {
  build(context: SerializationContext, map: SerializedData): CreateOpenAccessPassFromTicketSideEffect {
    return CreateOpenAccessPassFromTicketSideEffect.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);

    if(versionRange.contains(CreateOpenAccessPassFromTicketSideEffect.SCHEMA_VERSION)) {
      return map;
    }
    if(versionRange.isGreaterThan(CreateOpenAccessPassFromTicketSideEffect.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    // In this version the "passTemplateIds" field of type "string[]" was introduced
    if(version < 2) {
      map['passTemplateIds'] = [];
      version = 2;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
